import type { Mapper } from "./mappers/Mapper"
import { Mapper000 } from "./mappers/Mapper000"
import { Mapper001 } from "./mappers/Mapper001"

export enum Mirror {
    HARDWARE,
    Horizontal,
    Vertical,
    OneScreenLow,
    OneScreenHigh,
}

export class Cartridge {
    imageValid = false
    hardwareMirror = Mirror.Horizontal

    private mapperId = 0
    private programBanks = 0
    private characterBanks = 0

    private programMemory!: Uint8Array
    private characterMemory!: Uint8Array

    private mapper!: Mapper

    constructor(romData: Uint8Array) {
        const header = {
            name: romData.slice(0, 4),
            programRomChunks: romData[4]!,
            characterRomChunks: romData[5]!,
            mapper1: romData[6]!,
            mapper2: romData[7]!,
            programRamSize: romData[8]!,
            tvSystem1: romData[9]!,
            tvSystem2: romData[10]!,
            unused: romData.slice(11, 16),
        }

        let offset = 16
        if (header.mapper1 & 0x04) offset += 512

        this.mapperId = ((header.mapper2 >> 4) << 4) | (header.mapper1 >> 4)
        this.hardwareMirror = header.mapper1 & 0x01 ? Mirror.Vertical : Mirror.Horizontal

        let fileType: number = 1
        if ((header.mapper2 & 0x0c) === 0x08) fileType = 2

        if (fileType === 1) {
            this.programBanks = header.programRomChunks
            const programSize = this.programBanks * 16384
            this.programMemory = romData.slice(offset, offset + programSize)
            offset += programSize

            this.characterBanks = header.characterRomChunks
            if (this.characterBanks === 0) {
                this.characterMemory = new Uint8Array(8192)
            } else {
                const characterSize = this.characterBanks * 8192
                this.characterMemory = romData.slice(offset, offset + characterSize)
            }
        }

        if (fileType === 2) {
            this.programBanks = ((header.programRamSize & 0x07) << 8) | header.programRomChunks
            const programSize = this.programBanks * 16384
            this.programMemory = romData.slice(offset, offset + programSize)
            offset += programSize

            this.characterBanks = ((header.programRamSize & 0x38) << 8) | header.characterRomChunks
            const characterSize = this.characterBanks * 8192
            this.characterMemory = romData.slice(offset, offset + characterSize)
        }

        if (this.mapperId === 0) this.mapper = new Mapper000(this.programBanks, this.characterBanks)
        else if (this.mapperId === 1) this.mapper = new Mapper001(this.programBanks, this.characterBanks)

        this.imageValid = true
    }

    cpuRead(address: number) {
        const [mappedAddress, data] = this.mapper.cpuMapRead(address)

        if (mappedAddress !== undefined) {
            if (mappedAddress === 0xffffffff) {
                return data
            } else {
                return this.programMemory[mappedAddress]!
            }
        }
    }

    cpuWrite(address: number, data: number) {
        const mappedAddress = this.mapper.cpuMapWrite(address, data)

        if (mappedAddress !== undefined) {
            if (mappedAddress === 0xffffffff) {
                return true
            } else {
                this.programMemory[mappedAddress] = data
            }

            return true
        }

        return false
    }

    ppuRead(address: number) {
        const mappedAddress = this.mapper.ppuMapRead(address)

        if (mappedAddress !== undefined) {
            return this.characterMemory[mappedAddress]!
        }
    }

    ppuWrite(address: number, data: number) {
        const mappedAddress = this.mapper.ppuMapWrite(address)

        if (mappedAddress !== undefined) {
            this.characterMemory[mappedAddress] = data
            return true
        }

        return false
    }

    reset() {
        if (this.mapper) {
            this.mapper.reset()
        }
    }

    mirror() {
        const mirror = this.mapper.mirror()
        if (mirror === Mirror.HARDWARE) {
            return this.hardwareMirror
        } else {
            return mirror
        }
    }
}
